import {
	useCallback,
	useContext
} from 'react';
import createDataContext from '../services/createDataContext';

import uiReducer, {
	DISABLE_SIDEBAR,
	SET_API_ERROR_ALERT,
	SET_DOCS,
	SET_INFOBAR,
	SET_INFOBAR_LOADER,
	SET_NAV_LOCK,
	SET_ON_NAV_UNLOCK,
	SET_ON_TAG_MODAL_CLOSE,
	SET_SCREEN_LOADER,
	SET_SHOW_HELP,
	SET_SIDEBAR,
	SET_SIDEBAR_MENU,
	SET_TAG,
	SET_UNLOCK_NAV_CONFIRM
} from '../reducers/UIReducer';

export let controller = new AbortController();

const initialState = {
	apiErrorAlert: '',
	docs: [],
	infobar: null,
	infobarLoader: false,
	navLock: false,
	onNavUnlock: null,
	onTagModalClose: null,
	screenLoader: '',
	showHelp: JSON.parse( window.localStorage.getItem( 'SHOW_HELP' ) ) ?? true,
	sidebarMenu: '',
	sidebar: false,
	sidebarToggle: false,
	tag: null,
	unlockNavConfirm: false
};

const onInit = () => {
	controller = new AbortController();
};

const onCleanup = () => {
	controller.abort();
};

export const {
	StateContext,
	ActionContext,
	Provider
} = createDataContext( {
	reducer: uiReducer,
	initialState,
	controller,
	onInit,
	onCleanup
} );

export function useUIState() {
	const state = useContext( StateContext );

	return state;
}

export function useUIAction() {
	const dispatch = useContext( ActionContext );

	const disableSidebar = useCallback( () => {
		DISABLE_SIDEBAR( dispatch, controller.signal )();
	}, [ dispatch ] );

	const setApiErrorAlert = useCallback( error => {
		if ( error.message === 'canceled' ) {
			return;
		}

		SET_API_ERROR_ALERT( dispatch, controller.signal )( error );
	}, [ dispatch ] );

	const setDocs = useCallback( docs => {
		SET_DOCS( dispatch, controller.signal )( docs );
	}, [ dispatch ] );

	const setInfobar = useCallback( message => {
		SET_INFOBAR( dispatch, controller.signal )( message );
	}, [ dispatch ] );

	const setInfobarLoader = useCallback( message => {
		SET_INFOBAR_LOADER( dispatch, controller.signal )( message );
	}, [ dispatch ] );

	const setNavLock = useCallback( bool => {
		SET_NAV_LOCK( dispatch, controller.signal )( bool );
	}, [ dispatch ] );

	const setOnNavUnlock = useCallback( func => {
		SET_ON_NAV_UNLOCK( dispatch, controller.signal )( func );
	}, [ dispatch ] );

	const setOnTagModalClose = useCallback( func => {
		SET_ON_TAG_MODAL_CLOSE( dispatch, controller.signal )( func );
	}, [ dispatch ] );

	const setScreenLoader = useCallback( bool => {
		SET_SCREEN_LOADER( dispatch, controller.signal )( bool );
	}, [ dispatch ] );

	const setShowHelp = useCallback( bool => {
		SET_SHOW_HELP( dispatch, controller.signal )( bool );
		window.localStorage.setItem( 'SHOW_HELP', bool );
	}, [ dispatch ] );

	const setSidebar = useCallback( bool => {
		SET_SIDEBAR( dispatch, controller.signal )( bool );
	}, [ dispatch ] );

	const setSidebarMenu = useCallback( routes => {
		SET_SIDEBAR_MENU( dispatch, controller.signal )( routes );
	}, [ dispatch ] );

	const setTag = useCallback( ( tag = {
		id: 0
		// type: { id: 0 }
	} ) => {
		SET_TAG( dispatch, controller.signal )( tag );
	}, [ dispatch ] );

	const setUnlockNavConfirm = useCallback( func => {
		SET_UNLOCK_NAV_CONFIRM( dispatch, controller.signal )( func );
	}, [ dispatch ] );

	return {
		disableSidebar,
		setApiErrorAlert,
		setDocs,
		setInfobar,
		setInfobarLoader,
		setNavLock,
		setOnNavUnlock,
		setOnTagModalClose,
		setScreenLoader,
		setShowHelp,
		setSidebar,
		setSidebarMenu,
		setTag,
		setUnlockNavConfirm
	};
}
